/**
 * Renderer Stats Debug Component
 * Shows comprehensive WebGL/WebGPU renderer information for debugging memory leaks
 * Displays: draw calls, geometries, textures, programs, frame info, and memory usage
 * Features collapsible sections and localStorage persistence
 */

import { useThree, useFrame } from '@react-three/fiber'
import { useState, useRef, useCallback, memo, useEffect } from 'react'
import './RendererStats.css'

// Storage key for persisting collapse state
const STORAGE_KEY = 'renderer-stats-collapse-state'

// Default collapse state
const DEFAULT_COLLAPSE_STATE = {
  global: false,
  sections: {
    performance: true,
    mediaQuery: true,
    drawCalls: true,
    memory: true,
    shaders: true,
    jsHeap: true
  }
}

// Track JS heap memory over time for leak detection
const memoryHistoryRef = { current: [] }
const lastMemoryRef = { current: 0 }

// Module-level callback so RendererStatsUI manages its own state,
// completely decoupled from SceneContent (no 500ms setState in the scene tree)
let _rendererStatsCallback = null

// Get current active media query
function getActiveMediaQuery() {
  const width = window.innerWidth
  const isPortrait = window.innerHeight > window.innerWidth

  // Desktop breakpoints — must mirror getMediaQueryKey() in deviceDetection.js
  if (width >= 3840) return 'Desktop 4K (3840px+)'
  if (width >= 2560) return 'Desktop 1440 (2560px-3839px)'
  if (width >= 1920) return 'Desktop 1080 (1920px-2559px)'

  // Tablet breakpoints — Tablet Landscape now covers 900–1919 (the old
  // Desktop 720 1367–1919 tier was removed and folded in here).
  if (width >= 900 && width <= 1919 && !isPortrait) return 'Tablet Landscape (900px-1919px)'
  if (width >= 768 && width <= 1024 && isPortrait) return 'Tablet Portrait (768px-1024px)'

  // Mobile breakpoints
  if (width <= 899 && !isPortrait) return 'Mobile Landscape (<899px)'
  if (width <= 767 && isPortrait) return 'Mobile Portrait (<768px)'

  return 'Unknown'
}

// Load collapse state from localStorage
function loadCollapseState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      return { ...DEFAULT_COLLAPSE_STATE, ...JSON.parse(saved) }
    }
  } catch (e) {
    console.warn('Failed to load renderer stats collapse state:', e)
  }
  return DEFAULT_COLLAPSE_STATE
}

// Save collapse state to localStorage
function saveCollapseState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (e) {
    console.warn('Failed to save renderer stats collapse state:', e)
  }
}

// Internal component that runs inside Canvas context — uses module-level callback, no props needed
export function RendererStatsTracker() {
  const { gl } = useThree()
  const frameCountRef = useRef(0)
  const lastTimeRef = useRef(performance.now())
  const fpsHistoryRef = useRef([])
  const lastRenderCallsRef = useRef(0)
  const lastRenderTrianglesRef = useRef(0)
  const perFrameCallsRef = useRef(0)
  const perFrameTrianglesRef = useRef(0)

  useFrame(() => {
    frameCountRef.current++

    // Get renderer info
    const info = gl.info

    // Check for WebGPU renderer - must be inside useFrame to access gl
    const isWebGPU = gl.isWebGPURenderer || (gl.backend && gl.backend.isWebGPUBackend) || gl.constructor.name === 'WebGPURenderer'

    // Calculate per-frame draw calls by comparing to last frame
    // Note: WebGPU render info structure differs from WebGL
    if (info.render && !isWebGPU) {
      // WebGL path - standard render info
      const currentCalls = info.render.calls || 0
      const currentTriangles = info.render.triangles || 0

      // If counter reset or first frame, use current value
      if (currentCalls < lastRenderCallsRef.current) {
        perFrameCallsRef.current = currentCalls
        perFrameTrianglesRef.current = currentTriangles
      } else {
        perFrameCallsRef.current = currentCalls - lastRenderCallsRef.current
        perFrameTrianglesRef.current = currentTriangles - lastRenderTrianglesRef.current
      }

      lastRenderCallsRef.current = currentCalls
      lastRenderTrianglesRef.current = currentTriangles
    } else if (isWebGPU && info.render) {
      // WebGPU path - info.render exists but structure may differ
      // Try to access WebGPU-specific counters
      const currentCalls = info.render.drawCalls || info.render.calls || 0
      const currentTriangles = info.render.triangles || 0

      if (currentCalls < lastRenderCallsRef.current) {
        perFrameCallsRef.current = currentCalls
        perFrameTrianglesRef.current = currentTriangles
      } else {
        perFrameCallsRef.current = currentCalls - lastRenderCallsRef.current
        perFrameTrianglesRef.current = currentTriangles - lastRenderTrianglesRef.current
      }

      lastRenderCallsRef.current = currentCalls
      lastRenderTrianglesRef.current = currentTriangles
    }

    const now = performance.now()
    const delta = now - lastTimeRef.current

    // Update stats every 500ms to avoid excessive re-renders
    if (delta >= 500) {
      const fps = Math.round((frameCountRef.current / delta) * 1000)

      // Keep FPS history for averaging
      fpsHistoryRef.current.push(fps)
      if (fpsHistoryRef.current.length > 10) {
        fpsHistoryRef.current.shift()
      }
      const avgFps = Math.round(
        fpsHistoryRef.current.reduce((a, b) => a + b, 0) / fpsHistoryRef.current.length
      )

      // Memory info (if available)
      let memoryInfo = null
      if (info.memory) {
        memoryInfo = {
          geometries: info.memory.geometries || 0,
          textures: info.memory.textures || 0
        }
      }

      // Render info - use per-frame values
      let renderInfo = null
      if (info.render && !isWebGPU) {
        // WebGL path
        renderInfo = {
          calls: perFrameCallsRef.current,
          triangles: perFrameTrianglesRef.current,
          points: info.render.points || 0,
          lines: info.render.lines || 0,
          frame: info.render.frame || 0
        }
      } else if (isWebGPU) {
        // WebGPU path - may not have all counters
        renderInfo = {
          calls: perFrameCallsRef.current,
          triangles: perFrameTrianglesRef.current,
          points: info.render?.points ?? null,
          lines: info.render?.lines ?? null,
          frame: info.render?.frame ?? null,
          isWebGPU: true
        }
      }

      // Programs info - not applicable in WebGPU (uses pipelines instead)
      const programsCount = isWebGPU
        ? (info.pipelines?.length || info.programs?.length || 0)
        : (info.programs ? info.programs.length : 0)

      // Track JS heap memory delta for leak detection
      let jsHeapInfo = null
      let memoryDelta = 0
      let memoryTrend = 'stable'

      if (performance.memory) {
        const currentHeap = performance.memory.usedJSHeapSize
        const heapLimit = performance.memory.jsHeapSizeLimit

        // Calculate delta in MB per 500ms interval
        memoryDelta = (currentHeap - lastMemoryRef.current) / 1024 / 1024
        lastMemoryRef.current = currentHeap

        // Track history for trend analysis
        memoryHistoryRef.current.push(memoryDelta)
        if (memoryHistoryRef.current.length > 10) {
          memoryHistoryRef.current.shift()
        }

        // Calculate trend
        const avgDelta = memoryHistoryRef.current.reduce((a, b) => a + b, 0) / memoryHistoryRef.current.length
        if (avgDelta > 0.5) memoryTrend = 'LEAK!'
        else if (avgDelta > 0.1) memoryTrend = 'growing'
        else if (avgDelta < -0.5) memoryTrend = 'shrinking'
        else memoryTrend = 'stable'

        jsHeapInfo = {
          used: currentHeap,
          limit: heapLimit,
          delta: memoryDelta,
          trend: memoryTrend
        }
      }

      if (_rendererStatsCallback) {
        _rendererStatsCallback({
          fps,
          avgFps,
          memory: memoryInfo,
          jsHeap: jsHeapInfo,
          render: renderInfo,
          programs: programsCount,
          isWebGPU,
          mediaQuery: getActiveMediaQuery(),
          timestamp: now
        })
      }

      frameCountRef.current = 0
      lastTimeRef.current = now
    }
  })

  return null
}

// Section component for collapsible sections
const StatsSection = memo(function StatsSection({ title, children, isCollapsed, onToggle, className = '' }) {
  return (
    <div className={`renderer-stats-section ${className}`}>
      <div className="renderer-stats-section-header" onClick={onToggle}>
        <span>{title}</span>
        <span className={`renderer-stats-section-toggle ${isCollapsed ? 'collapsed' : ''}`}>▼</span>
      </div>
      <div className={`renderer-stats-section-content ${isCollapsed ? 'collapsed' : ''}`}>
        {children}
      </div>
    </div>
  )
})

// Memoized UI component — self-contained, manages its own stats state via module-level callback
const RendererStatsUIDisplay = memo(function RendererStatsUIDisplay() {
  const [stats, setStats] = useState(null)

  useEffect(() => {
    _rendererStatsCallback = setStats
    return () => { _rendererStatsCallback = null }
  }, [])
  const [isGlobalCollapsed, setIsGlobalCollapsed] = useState(() => loadCollapseState().global)
  const [sectionStates, setSectionStates] = useState(() => loadCollapseState().sections)

  // Save state to localStorage whenever it changes
  useEffect(() => {
    saveCollapseState({ global: isGlobalCollapsed, sections: sectionStates })
  }, [isGlobalCollapsed, sectionStates])

  // Toggle global collapse
  const toggleGlobalCollapse = useCallback(() => {
    setIsGlobalCollapsed(prev => !prev)
  }, [])

  // Toggle individual section
  const toggleSection = useCallback((sectionKey) => {
    setSectionStates(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }, [])

  if (!stats) return null

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A'
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  const getCallsColor = (calls) => {
    if (calls === null || calls === undefined) return '#888'
    if (calls > 500) return '#ff4444'
    if (calls > 200) return '#ffaa00'
    return '#44ff44'
  }

  const getTrianglesColor = (triangles) => {
    if (triangles === null || triangles === undefined) return '#888'
    if (triangles > 2000000) return '#ff4444'
    if (triangles > 1000000) return '#ffaa00'
    return '#44ff44'
  }

  const getFpsColor = (fps) => {
    if (fps < 30) return '#ff4444'
    if (fps < 50) return '#ffaa00'
    return '#44ff44'
  }

  return (
    <div className={`renderer-stats ${isGlobalCollapsed ? 'collapsed' : ''}`}>
      {/* Header with global toggle */}
      <div className="renderer-stats-header" onClick={toggleGlobalCollapse}>
        <span>🔧 RENDERER STATS</span>
        <span className={`renderer-stats-toggle ${isGlobalCollapsed ? 'collapsed' : ''}`}>▼</span>
      </div>

      {/* Minimized view - only shows when collapsed */}
      {isGlobalCollapsed && (
        <div className="renderer-stats-minimized">
          <div className="renderer-stats-minimized-row">
            <span className="renderer-stats-compact-label">FPS</span>
            <span className="renderer-stats-fps-value" style={{ color: getFpsColor(stats.fps) }}>
              {stats.fps}
            </span>
          </div>
          <div className="renderer-stats-minimized-row">
            <span className="renderer-stats-compact-label">Renderer</span>
            <span style={{ color: stats.isWebGPU ? '#44ff44' : '#ffaa00' }}>
              {stats.isWebGPU ? 'WebGPU' : 'WebGL'}
            </span>
          </div>
          <div className="renderer-stats-minimized-row">
            <span className="renderer-stats-compact-label">Media</span>
            <span className="renderer-stats-media-compact">{stats.mediaQuery}</span>
          </div>
        </div>
      )}

      {/* Full content - hidden when collapsed */}
      <div className={`renderer-stats-content ${isGlobalCollapsed ? 'collapsed' : ''}`}>
        {/* FPS Section */}
        <StatsSection
          title="PERFORMANCE"
          isCollapsed={!sectionStates.performance}
          onToggle={() => toggleSection('performance')}
        >
          <div className="renderer-stats-row">
            <span>FPS:</span>
            <span style={{ color: getFpsColor(stats.fps) }}>{stats.fps}</span>
          </div>
          <div className="renderer-stats-row">
            <span>Avg FPS:</span>
            <span style={{ color: getFpsColor(stats.avgFps) }}>{stats.avgFps}</span>
          </div>
          <div className="renderer-stats-row">
            <span>Renderer:</span>
            <span style={{ color: stats.isWebGPU ? '#44ff44' : '#ffaa00' }}>
              {stats.isWebGPU ? 'WebGPU' : 'WebGL'}
            </span>
          </div>
        </StatsSection>

        {/* Media Query Section */}
        <StatsSection
          title="MEDIA QUERY"
          isCollapsed={!sectionStates.mediaQuery}
          onToggle={() => toggleSection('mediaQuery')}
        >
          <div className="renderer-stats-media">{stats.mediaQuery}</div>
        </StatsSection>

        {/* Draw Calls Section */}
        {stats.render && (
          <StatsSection
            title="DRAW CALLS"
            isCollapsed={!sectionStates.drawCalls}
            onToggle={() => toggleSection('drawCalls')}
          >
            <div className="renderer-stats-row">
              <span>Calls:</span>
              <span style={{ color: getCallsColor(stats.render.calls) }}>
                {formatNumber(stats.render.calls)}
              </span>
            </div>
            {!stats.isWebGPU && (
              <>
                <div className="renderer-stats-row">
                  <span>Triangles:</span>
                  <span style={{ color: getTrianglesColor(stats.render.triangles) }}>
                    {formatNumber(stats.render.triangles)}
                  </span>
                </div>
                <div className="renderer-stats-row">
                  <span>Points:</span>
                  <span>{formatNumber(stats.render.points)}</span>
                </div>
                <div className="renderer-stats-row">
                  <span>Lines:</span>
                  <span>{formatNumber(stats.render.lines)}</span>
                </div>
                <div className="renderer-stats-row">
                  <span>Frame:</span>
                  <span>{stats.render.frame}</span>
                </div>
              </>
            )}
          </StatsSection>
        )}

        {/* Memory Section */}
        {stats.memory && (
          <StatsSection
            title="MEMORY"
            isCollapsed={!sectionStates.memory}
            onToggle={() => toggleSection('memory')}
          >
            <div className="renderer-stats-row">
              <span>Geometries:</span>
              <span style={{ color: stats.memory.geometries > 100 ? '#ff4444' : '#44ff44' }}>
                {stats.memory.geometries}
              </span>
            </div>
            <div className="renderer-stats-row">
              <span>Textures:</span>
              <span style={{ color: stats.memory.textures > 50 ? '#ff4444' : '#44ff44' }}>
                {stats.memory.textures}
              </span>
            </div>
          </StatsSection>
        )}

        {/* Programs Section */}
        <StatsSection
          title={stats.isWebGPU ? 'PIPELINES' : 'SHADERS'}
          isCollapsed={!sectionStates.shaders}
          onToggle={() => toggleSection('shaders')}
        >
          <div className="renderer-stats-row">
            <span>{stats.isWebGPU ? 'Pipelines:' : 'Programs:'}</span>
            <span style={{ color: stats.programs > 30 ? '#ff4444' : '#44ff44' }}>
              {stats.programs}
            </span>
          </div>
        </StatsSection>

        {/* JS Heap Section */}
        {stats.jsHeap && (
          <StatsSection
            title="JS HEAP"
            isCollapsed={!sectionStates.jsHeap}
            onToggle={() => toggleSection('jsHeap')}
            className="renderer-stats-js-heap"
          >
            <div className="renderer-stats-row">
              <span>Used:</span>
              <span>{(stats.jsHeap.used / 1024 / 1024).toFixed(1)} MB</span>
            </div>
            <div className="renderer-stats-row">
              <span>Δ/0.5s:</span>
              <span style={{
                color: stats.jsHeap.trend === 'LEAK!' ? '#ff4444' :
                  stats.jsHeap.trend === 'growing' ? '#ffaa00' :
                    stats.jsHeap.trend === 'shrinking' ? '#44ff44' : '#888'
              }}>
                {stats.jsHeap.delta > 0 ? '+' : ''}{stats.jsHeap.delta.toFixed(2)} MB
              </span>
            </div>
            <div className="renderer-stats-row">
              <span>Trend:</span>
              <span style={{
                color: stats.jsHeap.trend === 'LEAK!' ? '#ff4444' :
                  stats.jsHeap.trend === 'growing' ? '#ffaa00' :
                    stats.jsHeap.trend === 'shrinking' ? '#44ff44' : '#888',
                fontWeight: stats.jsHeap.trend === 'LEAK!' ? 'bold' : 'normal'
              }}>
                {stats.jsHeap.trend.toUpperCase()}
              </span>
            </div>
          </StatsSection>
        )}
      </div>
    </div>
  )
})

// UI component wrapper — self-contained, no props needed
export function RendererStatsUI() {
  return <RendererStatsUIDisplay />
}

// Combined component for easy usage (self-contained, no props needed)
export function RendererStats() {
  // Both Tracker and UI are now self-contained via module-level callback.
  // RendererStatsTracker must be placed inside the R3F Canvas;
  // RendererStatsUI can be placed anywhere in the React tree.
  return {
    Tracker: RendererStatsTracker,
    UI: RendererStatsUI
  }
}

export default RendererStats
